// Componente de notificaciones para trabajadores
// Muestra notificaciones cuando sus gastos son aprobados o rechazados

import React, { useState } from 'react';
import { Bell, Check, CheckCheck, X, AlertCircle, CheckCircle, RefreshCw } from 'lucide-react';
import { useNotificaciones } from '../../hooks/useNotificaciones';
import { useAuth } from '../../contexts/AuthContext';
import { formatDateTimeDDMMYYYY } from '../../utils/dateFormatter';

const Notificaciones: React.FC = () => {
  const { user } = useAuth();
  const {
    notificaciones,
    loading,
    error,
    notificacionesNoLeidas,
    marcarComoLeida,
    marcarTodasComoLeidas,
    recargar,
  } = useNotificaciones();

  const [isOpen, setIsOpen] = useState(false);

  // Solo mostrar para usuarios con rol 'Usuario'
  if (!user || user.rol !== 'Usuario') {
    return null;
  }

  const toggleDropdown = () => {
    setIsOpen(!isOpen);
  };

  const handleMarcarComoLeida = async (notificacionId: string) => {
    await marcarComoLeida(notificacionId);
  };

  const handleMarcarTodasComoLeidas = async () => {
    await marcarTodasComoLeidas();
  };

  return (
    <div className="relative">
      {/* Botón de notificaciones */}
      <button
        onClick={toggleDropdown}
        className="relative p-2 text-gray-600 hover:text-gray-900 hover:bg-gray-100 rounded-full transition-colors"
        aria-label="Notificaciones"
      >
        <Bell className="w-6 h-6" />
        {notificacionesNoLeidas > 0 && (
          <span className="absolute top-0 right-0 flex items-center justify-center w-5 h-5 text-xs font-bold text-white bg-red-500 rounded-full">
            {notificacionesNoLeidas > 9 ? '9+' : notificacionesNoLeidas}
          </span>
        )}
      </button>

      {/* Dropdown de notificaciones */}
      {isOpen && (
        <>
          {/* Overlay para cerrar al hacer click fuera */}
          <div
            className="fixed inset-0 z-40"
            onClick={() => setIsOpen(false)}
          />
          
          {/* Panel de notificaciones */}
          <div className="absolute right-0 mt-2 w-96 bg-white rounded-lg shadow-lg border border-gray-200 z-50 max-h-[600px] flex flex-col">
            {/* Header */}
            <div className="flex items-center justify-between p-4 border-b border-gray-200">
              <div className="flex items-center gap-2">
                <Bell className="w-5 h-5 text-gray-600" />
                <h3 className="text-lg font-semibold text-gray-900">
                  Notificaciones
                </h3>
                {notificacionesNoLeidas > 0 && (
                  <span className="px-2 py-1 text-xs font-bold text-white bg-red-500 rounded-full">
                    {notificacionesNoLeidas}
                  </span>
                )}
              </div>
              <div className="flex items-center gap-2">
                <button
                  onClick={recargar}
                  className="p-1 text-gray-600 hover:text-gray-900 hover:bg-gray-100 rounded transition-colors"
                  aria-label="Recargar notificaciones"
                  title="Recargar"
                >
                  <RefreshCw className="w-4 h-4" />
                </button>
                {notificacionesNoLeidas > 0 && (
                  <button
                    onClick={handleMarcarTodasComoLeidas}
                    className="p-1 text-gray-600 hover:text-gray-900 hover:bg-gray-100 rounded transition-colors"
                    aria-label="Marcar todas como leídas"
                    title="Marcar todas como leídas"
                  >
                    <CheckCheck className="w-4 h-4" />
                  </button>
                )}
                <button
                  onClick={() => setIsOpen(false)}
                  className="p-1 text-gray-600 hover:text-gray-900 hover:bg-gray-100 rounded transition-colors"
                  aria-label="Cerrar"
                >
                  <X className="w-4 h-4" />
                </button>
              </div>
            </div>

            {/* Contenido */}
            <div className="flex-1 overflow-y-auto">
              {loading && (
                <div className="flex items-center justify-center p-8">
                  <RefreshCw className="w-6 h-6 text-gray-400 animate-spin" />
                  <span className="ml-2 text-gray-600">Cargando notificaciones...</span>
                </div>
              )}

              {error && (
                <div className="p-4 m-4 bg-red-50 border border-red-200 rounded-lg">
                  <div className="flex items-center gap-2 text-red-800">
                    <AlertCircle className="w-5 h-5" />
                    <span className="text-sm font-medium">Error al cargar notificaciones</span>
                  </div>
                  <p className="mt-1 text-sm text-red-600">{error}</p>
                </div>
              )}

              {!loading && !error && notificaciones.length === 0 && (
                <div className="flex flex-col items-center justify-center p-8 text-gray-500">
                  <Bell className="w-12 h-12 mb-2 text-gray-300" />
                  <p className="text-sm">No hay notificaciones</p>
                </div>
              )}

              {!loading && !error && notificaciones.length > 0 && (
                <div className="divide-y divide-gray-200">
                  {notificaciones.map((notificacion) => (
                    <div
                      key={notificacion.id}
                      className={`p-4 hover:bg-gray-50 transition-colors ${
                        !notificacion.leida ? 'bg-blue-50' : ''
                      }`}
                    >
                      <div className="flex items-start gap-3">
                        {/* Icono según tipo */}
                        <div className="flex-shrink-0 mt-1">
                          {notificacion.tipo === 'aprobado' ? (
                            <CheckCircle className="w-5 h-5 text-green-500" />
                          ) : (
                            <X className="w-5 h-5 text-red-500" />
                          )}
                        </div>

                        {/* Contenido */}
                        <div className="flex-1 min-w-0">
                          <p
                            className={`text-sm ${
                              !notificacion.leida
                                ? 'font-semibold text-gray-900'
                                : 'text-gray-700'
                            }`}
                          >
                            {notificacion.mensaje}
                          </p>
                          <p className="mt-1 text-xs text-gray-500">
                            {formatDateTimeDDMMYYYY(notificacion.created_at)}
                          </p>
                        </div>

                        {/* Botón marcar como leída */}
                        {!notificacion.leida && (
                          <button
                            onClick={() => handleMarcarComoLeida(notificacion.id)}
                            className="flex-shrink-0 p-1 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded transition-colors"
                            aria-label="Marcar como leída"
                            title="Marcar como leída"
                          >
                            <Check className="w-4 h-4" />
                          </button>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        </>
      )}
    </div>
  );
};

export default Notificaciones;

